
import { useState } from "react";
import { Module } from "@/types/course";
import { Button } from "@/components/ui/button";
import { Pencil, Plus, Trash2, GripVertical, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface ModuleListProps {
  courseId: string;
  modules: Module[];
  onModulesChange: (modules: Module[]) => void;
  selectedModuleId: string | null;
  onSelectModule: (moduleId: string) => void;
}

export function ModuleList({ 
  courseId, 
  modules, 
  onModulesChange, 
  selectedModuleId, 
  onSelectModule 
}: ModuleListProps) {
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [editModuleTitle, setEditModuleTitle] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  const toggleModuleExpanded = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleAddModule = async () => {
    if (!newModuleTitle.trim()) {
      toast.error("Module title is required");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("modules")
        .insert({
          course_id: courseId,
          title: newModuleTitle,
          order: modules.length
        })
        .select()
        .single();

      if (error) throw error;

      const newModule = data as Module;
      const updatedModules = [...modules, newModule];
      onModulesChange(updatedModules);
      setNewModuleTitle("");
      setIsAddingModule(false);
      toast.success("Module added successfully");
    } catch (error) {
      console.error("Error adding module:", error);
      toast.error("Failed to add module");
    }
  };

  const handleEditModule = (module: Module) => {
    setEditingModuleId(module.id);
    setEditModuleTitle(module.title);
  };

  const saveModuleEdit = async () => {
    if (!editModuleTitle.trim()) {
      toast.error("Module title is required");
      return;
    }

    try {
      const { error } = await supabase
        .from("modules")
        .update({ title: editModuleTitle })
        .eq("id", editingModuleId);

      if (error) throw error;

      const updatedModules = modules.map(module => 
        module.id === editingModuleId 
          ? { ...module, title: editModuleTitle } 
          : module
      );
      
      onModulesChange(updatedModules);
      setEditingModuleId(null);
      toast.success("Module updated successfully");
    } catch (error) {
      console.error("Error updating module:", error);
      toast.error("Failed to update module");
    }
  };

  const confirmDeleteModule = (module: Module) => {
    setModuleToDelete(module);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteModule = async () => {
    if (!moduleToDelete) return;

    try {
      const { error } = await supabase
        .from("modules")
        .delete()
        .eq("id", moduleToDelete.id);

      if (error) throw error;

      const updatedModules = modules.filter(m => m.id !== moduleToDelete.id);
      onModulesChange(updatedModules);
      
      // If the deleted module was selected, select another one if available
      if (selectedModuleId === moduleToDelete.id) {
        const nextModule = updatedModules[0];
        if (nextModule) {
          onSelectModule(nextModule.id);
        } else {
          onSelectModule("");
        }
      }
      
      setIsDeleteDialogOpen(false);
      setModuleToDelete(null);
      toast.success("Module deleted successfully");
    } catch (error) {
      console.error("Error deleting module:", error);
      toast.error("Failed to delete module");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Modules</h3>
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => setIsAddingModule(true)}
        >
          <Plus className="h-4 w-4 mr-2" /> Add Module
        </Button>
      </div>

      <div className="space-y-2">
        {modules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No modules yet. Add your first module to get started.
          </div>
        ) : (
          modules.map((module) => (
            <div 
              key={module.id}
              className={`border rounded-md overflow-hidden ${selectedModuleId === module.id ? 'border-primary/50 bg-primary/5' : ''}`}
            >
              <div 
                className="flex items-center justify-between p-3 cursor-pointer"
                onClick={() => onSelectModule(module.id)}
              >
                <div className="flex items-center gap-2 flex-1">
                  <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                  <div 
                    className="flex items-center gap-1.5 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleModuleExpanded(module.id);
                    }}
                  >
                    {expandedModules[module.id] ? 
                      <ChevronDown className="h-4 w-4" /> : 
                      <ChevronRight className="h-4 w-4" />
                    }
                  </div>
                  
                  {editingModuleId === module.id ? (
                    <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                      <Input 
                        value={editModuleTitle}
                        onChange={(e) => setEditModuleTitle(e.target.value)}
                        className="h-8"
                        autoFocus
                      />
                      <Button size="sm" onClick={saveModuleEdit}>Save</Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setEditingModuleId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <span className="font-medium">{module.title}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditModule(module);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-8 w-8 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDeleteModule(module);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {expandedModules[module.id] && module.lessons && module.lessons.length > 0 && (
                <div className="border-t bg-muted/30 px-3 py-2">
                  <div className="pl-8 space-y-1">
                    {module.lessons.map(lesson => (
                      <div 
                        key={lesson.id}
                        className="text-sm py-1.5 px-2 rounded hover:bg-muted cursor-pointer"
                      >
                        {lesson.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Module Dialog */}
      <Dialog open={isAddingModule} onOpenChange={setIsAddingModule}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Module</DialogTitle>
            <DialogDescription>
              Modules help you organize your course content into logical sections.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="moduleTitle" className="text-sm font-medium">Module Title</label>
              <Input 
                id="moduleTitle"
                placeholder="Enter module title" 
                value={newModuleTitle}
                onChange={(e) => setNewModuleTitle(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleAddModule}>Add Module</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the module "{moduleToDelete?.title}" and all its lessons. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteModule}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
